import type { Bootstrap, GameSnapshot, GameSummary, Identity, Theme } from './types';

/**
 * Talks to api.php.
 *
 * Every move is a POST here rather than a write to Firebase. The server is the
 * only thing that changes game state, so an illegal move is rejected with a
 * reason instead of briefly becoming visible to everyone and being corrected
 * afterwards.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The lock was held; the caller should simply try again. */
  get isBusy(): boolean {
    return this.status === 409;
  }

  get isUnauthorised(): boolean {
    return this.status === 401;
  }
}

const ENDPOINT = 'api.php';

async function call<T>(
  action: string,
  body?: Record<string, unknown>,
  method: 'GET' | 'POST' = 'GET',
  params: Record<string, string> = {},
): Promise<T> {
  // Extra parameters go here rather than being appended to the action name.
  // Doing the latter and then encoding produced an action literally called
  // "state&gameId=abc123", which the server quite reasonably rejected.
  const query = new URLSearchParams({ action, ...params });
  const url = `${ENDPOINT}?${query.toString()}`;

  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: method === 'POST' ? { 'Content-Type': 'application/json' } : {},
    body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
  });

  let payload: Record<string, unknown>;
  try {
    payload = await response.json();
  } catch {
    // A non-JSON body means PHP fell over before it could format a reply.
    throw new ApiError(
      `The server returned something unreadable (HTTP ${response.status}).`,
      response.status,
      'unreadable',
    );
  }

  if (!response.ok || payload.ok === false) {
    throw new ApiError(
      String(payload.error ?? 'Something went wrong.'),
      response.status,
      String(payload.code ?? 'error'),
    );
  }

  return payload as T;
}

/** Retry once on a lock conflict, which is contention rather than failure. */
async function callRetrying<T>(
  action: string,
  body: Record<string, unknown>,
  attempts = 3,
): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await call<T>(action, body, 'POST');
    } catch (error) {
      if (i >= attempts - 1 || !(error instanceof ApiError) || !error.isBusy) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, 150 * (i + 1)));
    }
  }
}

export const api = {
  bootstrap: () => call<{ ok: true } & Bootstrap>('config'),

  me: () => call<{ ok: true } & Identity>('me'),

  // The theme travels with sign-in so the code is checked against the list for
  // the front door it was asked from -- a lab address cannot get in at the
  // family entrance.
  sendCode: (email: string, themeId: string) =>
    call<{ ok: true; message: string }>('send-code', { email, themeId }, 'POST'),

  verifyCode: (email: string, code: string, themeId: string) =>
    call<{ ok: true } & Identity>('verify-code', { email, code, themeId }, 'POST'),

  members: (themeId: string) =>
    call<{ ok: true; themeId: string; members: string[] }>('members', undefined, 'GET', { themeId }),

  memberAdd: (themeId: string, email: string) =>
    call<{ ok: true; members: string[] }>('member-add', { themeId, email }, 'POST'),

  memberRemove: (themeId: string, email: string) =>
    call<{ ok: true; members: string[] }>('member-remove', { themeId, email }, 'POST'),

  logout: () => call<{ ok: true }>('logout', {}, 'POST'),

  games: () => call<{ ok: true; games: GameSummary[] }>('games'),

  /** Only this theme's games -- each theme has its own lobby. */
  gamesOf: (themeId: string) =>
    call<{ ok: true; games: GameSummary[] }>('games', undefined, 'GET', { themeId }),

  state: (gameId: string) =>
    call<{ ok: true } & GameSnapshot>('state', undefined, 'GET', { gameId }),

  create: (themeId?: string) =>
    callRetrying<{ ok: true; gameId: string; themeId: string }>('create', { themeId }),

  join: (gameId: string, suspectId: string, bot?: { difficulty: string }) =>
    callRetrying<{ ok: true }>('join', {
      gameId,
      suspectId,
      ...(bot ? { isBot: true, botDifficulty: bot.difficulty } : {}),
    }),

  start: (gameId: string) => callRetrying<{ ok: true }>('start', { gameId }),

  act: (gameId: string, type: string, payload: Record<string, unknown> = {}) =>
    callRetrying<{ ok: true }>('act', { gameId, type, payload }),

  /**
   * Nudge the server to play any pending bot turns.
   *
   * Deliberately not callRetrying: a 409 here means another request already
   * holds the lock and is doing the exact work this call was asking for. For a
   * player's move, contention is worth retrying because the move must
   * eventually land. For a nudge, busy *is* success -- retrying only piles more
   * requests onto a server that is already handling it.
   */
  tick: (gameId: string) => call<{ ok: true }>('tick', { gameId }, 'POST'),

  saveNotes: (gameId: string, notes: Record<string, string>) =>
    callRetrying<{ ok: true; saved: number }>('notes', { gameId, notes }),

  purge: (gameId: string) => callRetrying<{ ok: true }>('purge', { gameId }),
};

const themeCache = new Map<string, Promise<Theme>>();

/**
 * Themes are static files, so the browser and its cache handle them; there is
 * no reason to route board layout through PHP.
 */
export function loadTheme(themeId: string): Promise<Theme> {
  let pending = themeCache.get(themeId);

  if (!pending) {
    pending = fetch(`themes/${encodeURIComponent(themeId)}/theme.json`).then((response) => {
      if (!response.ok) {
        throw new Error(`Could not load the "${themeId}" theme (HTTP ${response.status}).`);
      }
      return response.json() as Promise<Theme>;
    });
    themeCache.set(themeId, pending);
  }

  return pending;
}
